import { useMemo, useState } from 'react';

import {
  ColumnFiltersState,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useParams } from 'react-router-dom';

import { Permission } from '../../../../../../../backend/src/auth/permissions/permission';
import { RunType } from '../../../../../../../backend/src/run/run-type';
import { RunStatus } from '../../../../../../../backend/src/types/types';
import { formatDuration } from '../../../../../helpers/formatDate';
import { AllRunTypes, RunTypeLabels } from '../../../../../helpers/runs';
import { AllRunStatuses, RunStatusBadgeClasses, RunStatusLabels } from '../../../../lib/pipeline-status';
import { formatDatetimeForTable, includedInArr } from '../../../../lib/table-utils';
import { useFetchOrgRuns } from '@/api/queries/analyticsQueries/useFetchOrgRuns';
import useGetOrganisation from '@/api/queries/useGetOrganisation';
import { OrganisationRun } from '@/api/types/backend-dto-types';
import { usePlatformPermissionCheck } from '@/hooks/usePlatformPermissionCheck';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { FilterMultiSelect } from '@/new/components/filter/filter-multi-select';
import { FilterPopoverContent } from '@/new/components/filter-popover-content';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { intervalToMinutes } from '@/new/components/processing-status-badge';
import { Badge } from '@/new/components/shadcn/badge';
import { ScrollableTable } from '@/new/components/table/scrollable-table';
import { CellWithCopy } from '@/new/components/table/table-cell-with-copy';

const fallbackTableData: OrganisationRun[] = [];

const formatRunStatus = (status: string) => RunStatusLabels[status as RunStatus] ?? status;
const formatMinutes = (totalMinutes: number) => formatDuration(totalMinutes * 60_000);
const formatRunType = (type: string) => RunTypeLabels[type as RunType] ?? type;

const columnHelper = createColumnHelper<OrganisationRun>();
const columns = [
  columnHelper.accessor('id', {
    header: () => <span>Run Id</span>,
    cell: (info) => (
      <CellWithCopy textToCopy={info.getValue()}>
        <span className="line-clamp-1 max-w-42 break-all">{info.getValue()}</span>
      </CellWithCopy>
    ),
  }),
  columnHelper.accessor('matterId', {
    header: () => <span>Matter Id</span>,
    cell: (info) => (
      <CellWithCopy textToCopy={info.getValue()}>
        <span className="line-clamp-1 max-w-42 break-all">{info.getValue()}</span>
      </CellWithCopy>
    ),
  }),
  columnHelper.accessor('matterName', {
    header: () => <span>Matter</span>,
    cell: (info) => <span className="line-clamp-1 max-w-48">{info.getValue()}</span>,
  }),
  columnHelper.accessor('type', {
    header: () => <span>Type</span>,
    cell: (info) => <span className="whitespace-nowrap">{formatRunType(info.getValue())}</span>,
    filterFn: includedInArr,
  }),
  columnHelper.accessor('status', {
    header: () => <span>Status</span>,
    cell: (info) => {
      const status = info.getValue() as RunStatus;
      return (
        <Badge variant="outline" className={RunStatusBadgeClasses[status]}>
          {formatRunStatus(status)}
        </Badge>
      );
    },
    filterFn: includedInArr,
  }),
  columnHelper.accessor('docCount', {
    header: () => <span>Docs</span>,
    cell: (info) => <span>{info.getValue()}</span>,
  }),
  columnHelper.accessor('successfulPagesCount', {
    header: () => <span>Pages Succeeded</span>,
    cell: (info) => <span>{info.getValue()}</span>,
  }),
  columnHelper.accessor('failedPagesCount', {
    header: () => <span>Pages Failed</span>,
    cell: (info) => {
      const failed = info.getValue();
      return <span className={failed > 0 ? 'text-red-600' : undefined}>{failed}</span>;
    },
  }),
  columnHelper.accessor('createdBy.email', {
    id: 'createdBy.email',
    header: () => <span>Created By</span>,
    cell: (info) => <span>{info.getValue()}</span>,
    filterFn: includedInArr,
  }),
  columnHelper.accessor((row) => intervalToMinutes(row.durationEstimate), {
    id: 'durationEstimate',
    header: () => <span>Est. Duration</span>,
    cell: (info) => {
      const minutes = info.getValue();
      return <span className="whitespace-nowrap">{minutes === null ? '—' : formatMinutes(minutes)}</span>;
    },
  }),
  // Actual duration isn't stored; derive it from the two timestamps and leave it blank
  // until the run has actually finished.
  columnHelper.accessor(
    (row) =>
      row.completedAt
        ? Math.round((new Date(row.completedAt).getTime() - new Date(row.createdDate).getTime()) / 60_000)
        : null,
    {
      id: 'actualDuration',
      header: () => <span>Duration</span>,
      cell: (info) => {
        const minutes = info.getValue();
        return <span className="whitespace-nowrap">{minutes === null ? '—' : formatMinutes(minutes)}</span>;
      },
    },
  ),
  columnHelper.accessor('createdDate', {
    header: () => <span>Date Created</span>,
    cell: (info) => <span className="whitespace-nowrap">{formatDatetimeForTable(info.getValue())}</span>,
  }),
  columnHelper.accessor('completedAt', {
    header: () => <span>Date Completed</span>,
    cell: (info) => {
      const value = info.getValue();
      return <span className="whitespace-nowrap">{value ? formatDatetimeForTable(value) : '—'}</span>;
    },
  }),
];

export function OrganisationRuns() {
  const { organisationId } = useParams();
  if (!organisationId) return null;

  usePlatformPermissionCheck(Permission.PlatformManageRuns);

  const { data: organisation } = useGetOrganisation(organisationId);
  const { data: runsData, isLoading } = useFetchOrgRuns(organisationId);

  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  // Only offer statuses and users actually present, so the dropdowns don't list dead options.
  const statusOptions = useMemo(() => {
    const present = new Set((runsData ?? fallbackTableData).map((run) => run.status));
    return AllRunStatuses.filter((status) => present.has(status));
  }, [runsData]);

  const typeOptions = useMemo(() => {
    const present = new Set((runsData ?? fallbackTableData).map((run) => run.type));
    return AllRunTypes.filter((type) => present.has(type));
  }, [runsData]);

  // The backend nulls out matter names the viewer isn't entitled to — typically every
  // one, since this is a platform page — so drop the column rather than show blanks.
  const columnVisibility = useMemo(
    () => ({ matterName: (runsData ?? fallbackTableData).some((run) => run.matterName !== null) }),
    [runsData],
  );

  const userOptions = useMemo(() => {
    const emails = (runsData ?? fallbackTableData).map((run) => run.createdBy.email).filter(Boolean);
    return Array.from(new Set(emails)).sort((a, b) => a.localeCompare(b));
  }, [runsData]);

  const table = useReactTable({
    data: runsData ?? fallbackTableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    initialState: {
      sorting: [
        {
          id: 'createdDate',
          desc: true,
        },
      ],
      pagination: {
        pageSize: 15,
      },
    },
    state: {
      globalFilter,
      columnFilters,
      columnVisibility,
    },
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
  });

  const clearAllFilters = () => {
    setGlobalFilter('');
    setColumnFilters([]);
  };

  const statusFilterValue = (table.getColumn('status')?.getFilterValue() as string[]) ?? [];
  const typeFilterValue = (table.getColumn('type')?.getFilterValue() as string[]) ?? [];
  const userFilterValue = (table.getColumn('createdBy.email')?.getFilterValue() as string[]) ?? [];

  return (
    <>
      <Breadcrumb
        items={
          organisation
            ? [
                { href: '/admin/organisations', label: 'Admin', active: false },
                {
                  href: `/admin/organisations/${organisation.id}`,
                  label: organisation.name,
                  active: false,
                },
                {
                  href: `/admin/organisations/${organisation.id}/runs`,
                  label: 'Runs',
                  active: true,
                },
              ]
            : [{ href: '/admin/organisations', label: 'Admin', active: false }]
        }
      />
      <PageContainer>
        <PageHeader>
          <PageTitle text="Runs" />
        </PageHeader>
      </PageContainer>

      <ScrollableTable
        table={table}
        plural="runs"
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        loading={isLoading}
        filters={{
          popover: (
            <FilterPopoverContent clearAllFilters={clearAllFilters}>
              <div>
                <p className="mb-3 text-gray-600">Status</p>
                <FilterMultiSelect
                  values={statusFilterValue}
                  options={statusOptions}
                  column={table.getColumn('status')!}
                  placeholder="Select statuses..."
                  formatLabel={formatRunStatus}
                />
              </div>
              <div>
                <p className="mb-3 text-gray-600">Type</p>
                <FilterMultiSelect
                  values={typeFilterValue}
                  options={typeOptions}
                  column={table.getColumn('type')!}
                  placeholder="Select types..."
                  formatLabel={formatRunType}
                />
              </div>
              <div>
                <p className="mb-3 text-gray-600">Created By</p>
                <FilterMultiSelect
                  values={userFilterValue}
                  options={userOptions}
                  column={table.getColumn('createdBy.email')!}
                  placeholder="Select users..."
                />
              </div>
            </FilterPopoverContent>
          ),
          count: columnFilters.length,
          clearAll: clearAllFilters,
        }}
      />
    </>
  );
}
